import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {isSuperAdmin} from '../../helpers/helpers';
import {buildValidationRequest, formatValidationDate, localDateInput, validationRequest} from './validationApi';
import './SensorValidation.scss';

export const validationLabels = {
    RELIABLE: 'Reliable', USABLE_WITH_WARNINGS: 'Usable with warnings', UNRELIABLE: 'Unreliable',
    NOT_APPLICABLE: 'Not applicable', STABLE: 'Stable', STABLE_WITH_GAPS: 'Stable with gaps',
    UNSTABLE: 'Unstable', INACTIVE: 'Inactive', NO_DATA: 'No data', VALID: 'Valid', WARNING: 'Warning',
    INVALID: 'Invalid', INSUFFICIENT_REFERENCE: 'Insufficient reference',
    SUN_EXPOSED_SUSPECT: 'Suspected sun exposure', INDOOR_SUSPECT: 'Suspected indoor installation',
    STUCK_VALUES: 'Stuck values', OUT_OF_RANGE_READINGS: 'Out of range readings',
    LOW_CORRELATION: 'Low correlation', CONSISTENT_NIGHT_OFFSET: 'Consistent night offset',
    HUMIDITY_SATURATED: 'Humidity saturated', COLDEST_IN_GROUP: 'Coldest in group',
    COLDER_THAN_GROUP: 'Colder than group', TYPICAL: 'Typical', WARMER_THAN_GROUP: 'Warmer than group',
    WARMEST_IN_GROUP: 'Warmest in group'
};

const stabilityMetrics = {
    score: 'Stability score', baseIntervalMin: 'Base interval (min)', expected: 'Expected readings',
    received: 'Received readings', duplicates: 'Duplicate readings', deliveryRatio: 'Delivery ratio (%)',
    dataSource: 'Measurement data source', dataSourceGuessed: 'Data source guessed',
    baseIntervalOccurrences: 'Base interval occurrences', baseIntervalShare: 'Base interval share (%)',
    smallestObservedIntervalMin: 'Smallest observed interval (min)', intervals: 'Intervals',
    onTimeIntervals: 'On-time intervals', onTimeShare: 'On-time intervals (%)',
    increaseEvents: 'Interval increases', increasesPerDay: 'Interval increases per day', missedMessages: 'Missed messages',
    hourlyCoverage: 'Hourly coverage (%)', longestGapMin: 'Longest gap (min)',
    gapsOverOneHour: 'Gaps over one hour', lastSeen: 'Last seen', effectiveFrom: 'Effective from'
};
const temperatureMetrics = {
    relevanceScore: 'Temperature relevance score', nightHours: 'Comparable night hours', dayHours: 'Comparable day hours',
    nightMedianDev: 'Night median deviation (°C)', nightP90AbsDev: 'Night P90 absolute deviation (°C)',
    nightBigDevShare: 'Large night deviations (%)', dayMedianDev: 'Day median deviation (°C)',
    dayP90AbsDev: 'Day P90 absolute deviation (°C)', dayBigDevShare: 'Large day deviations (%)',
    correlation: 'Correlation', referencePoolSize: 'Reference pool size', invalidReadings: 'Invalid readings',
    stuckHours: 'Hours with stuck values', stuckValue: 'Stuck temperature (°C)'
};
const percentages = ['deliveryRatio', 'baseIntervalShare', 'onTimeShare', 'hourlyCoverage', 'nightBigDevShare', 'dayBigDevShare',
    'nightColdestShare', 'nightWarmestShare', 'avgNightRankPercentile'];

const IntervalHistogram = ({rows, t}) => {
    if (!rows || rows.length === 0) return null;
    const maximum = Math.max(1, ...rows.map(row => row.count));
    return <div className='validationHistogram'>
        <h4>{t.gettext('Measurement intervals')}</h4>
        <ul aria-label={t.gettext('Measurement intervals')}>
            {rows.map((row, index) => <li key={index}>
                <span className='validationHistogramLabel'>{row.label}</span>
                <span className='validationHistogramTrack' aria-hidden='true'>
                    <span className='validationHistogramBar' style={{width: `${row.count > 0 ? Math.max(2, row.count / maximum * 100) : 0}%`}}/>
                </span>
                <span className='validationHistogramCount'>{row.count.toLocaleString(localStorage.getItem('lang') || 'en')} {t.gettext('intervals')}</span>
            </li>)}
        </ul>
    </div>;
};

const SensorValidation = () => {
    const t = useSelector(state => state.language.translator);
    const admin = isSuperAdmin();
    const [form, setForm] = useState(() => {
        const now = new Date();
        return {mode: 'USER', userId: '', lat: '', lon: '', radiusKm: '20', maxSensors: '30',
            maxSensorsPerSite: '', periodFrom: localDateInput(new Date(now - 14 * 86400000)), periodTo: localDateInput(now)};
    });
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState(false);
    const [report, setReport] = useState(null);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const requestRef = useRef(null);
    useEffect(() => () => requestRef.current && requestRef.current.abort(), []);
    useEffect(() => {
        if (!admin || form.mode !== 'USER' || search.trim().length < 3 || selectedUser) return;
        const controller = new AbortController();
        setUsersLoading(true);
        setUsersError(false);
        const timer = setTimeout(() => {
            validationRequest(`/custom/users/user_list_by_name?search_query=${encodeURIComponent(search.trim())}`, controller.signal)
                .then(data => {if (!controller.signal.aborted) setUsers(data);})
                .catch(e => {if (e.name !== 'AbortError') setUsersError(true);})
                .finally(() => {if (!controller.signal.aborted) setUsersLoading(false);});
        }, 300);
        return () => {clearTimeout(timer); controller.abort();};
    }, [admin, search, form.mode, selectedUser]);
    const needsUserSelection = admin && form.mode === 'USER' && search.trim() !== '' && !selectedUser;
    const userName = user => user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    const searchUsers = event => {
        setSearch(event.target.value);
        setSelectedUser(null);
        setForm(current => ({...current, userId: ''}));
        setUsers([]);
        setUsersError(false);
        setUsersLoading(event.target.value.trim().length >= 3);
    };
    const change = event => setForm({...form, [event.target.name]: event.target.value});
    const label = value => t.gettext(validationLabels[value] || value || 'No data');
    const date = value => formatValidationDate(value, localStorage.getItem('lang') || 'en');
    const value = (key, raw) => {
        const metric = raw && typeof raw === 'object' ? raw.parsedValue : raw;
        if (metric == null) return '—';
        if (['lastSeen', 'effectiveFrom'].includes(key)) return date(metric);
        if (typeof metric === 'boolean') return t.gettext(metric ? 'Yes' : 'No');
        return typeof metric === 'number' ? (metric * (percentages.includes(key) ? 100 : 1))
            .toLocaleString(localStorage.getItem('lang') || 'en', {maximumFractionDigits: 2}) : metric;
    };
    const metrics = (data, fields) => <dl className='validationMetrics'>{Object.entries(fields).map(([key, title]) =>
        <div key={key}><dt>{t.gettext(title)}</dt><dd>{value(key, data[key])}</dd></div>)}</dl>;
    const submit = async event => {
        event.preventDefault();
        if (pending || needsUserSelection) return;
        let body;
        try {body = buildValidationRequest(form, admin);} catch (e) {setError(e.message); return;}
        const controller = new AbortController();
        requestRef.current = controller;
        setPending(true); setError(''); setReport(null);
        try {setReport(await validationRequest('/panel/sensor_validation/report', controller.signal, body));}
        catch (e) {if (e.name !== 'AbortError') setError(e.message === '401' || e.message === '403' ? 'You do not have permission to generate this report.' : 'Unable to generate validation report. Check the parameters and try again.');}
        finally {if (!controller.signal.aborted) {setPending(false); requestRef.current = null;}}
    };
    const input = (name, title, type = 'number', props = {}) => <label>{t.gettext(title)}
        <input name={name} type={type} value={form[name]} onChange={change} {...props}/></label>;
    return <section className='sensorValidation'>
        <h1>{t.gettext('Sensor validation report')}</h1>
        <form onSubmit={submit}>
            <fieldset disabled={pending}>
                <legend>{t.gettext('Report parameters')}</legend>
                <div className='validationForm'>
                    {admin && <label>{t.gettext('Selection mode')}<select name='mode' value={form.mode} onChange={change}>
                        <option value='USER'>{t.gettext('By user')}</option><option value='LOCATION'>{t.gettext('By location')}</option>
                    </select></label>}
                    {form.mode === 'USER' && (admin ? <div className='validationUser'>
                        <label>{t.gettext('Search users')}<input type='search' value={search} onChange={searchUsers}
                            aria-describedby='validation-user-help' autoComplete='off'/></label>
                        {!search.trim() ? <small id='validation-user-help'>{t.gettext('The currently logged-in user will be used.')}</small>
                            : selectedUser ? <p id='validation-user-help' role='status'>{t.gettext('Selected user')}: <strong>{userName(selectedUser)} — {selectedUser.email} (#{selectedUser.id})</strong></p>
                            : <>
                                <small id='validation-user-help'>{t.gettext('Select a user from the search results to generate a report.')}</small>
                                {search.trim().length < 3 ? <p role='status'>{t.gettext('Type at least 3 characters to search users.')}</p>
                                    : usersLoading ? <p role='status'>{t.gettext('Loading users...')}</p>
                                    : usersError ? <p role='alert'>{t.gettext('Unable to load users. Try searching again.')}</p>
                                        : users.length === 0 ? <p role='status'>{t.gettext('No matching users found.')}</p>
                                            : <ul className='validationUserResults' aria-label={t.gettext('Matching users')}>
                                                {users.map(user => <li key={user.id}><button type='button' onClick={() => {
                                                    setSelectedUser(user);
                                                    setForm(current => ({...current, userId: String(user.id)}));
                                                }}>{userName(user)} — {user.email} (#{user.id})</button></li>)}
                                            </ul>}
                            </>}
                    </div> : <p>{t.gettext('My sensors')}</p>)}
                    {admin && form.mode === 'LOCATION' && <>
                        {input('lat', 'Latitude', 'number', {required: true, min: -90, max: 90, step: 'any'})}
                        {input('lon', 'Longitude', 'number', {required: true, min: -180, max: 180, step: 'any'})}
                        {input('radiusKm', 'Radius (km)', 'number', {required: true, min: 0.01, max: 100, step: 'any'})}
                        {input('maxSensors', 'Maximum sensors', 'number', {required: true, min: 1, max: 500})}
                        {input('maxSensorsPerSite', 'Maximum sensors per site (optional)', 'number', {min: 1})}
                    </>}
                    {input('periodFrom', 'Period from', 'datetime-local', {required: true})}
                    {input('periodTo', 'Period to (exclusive)', 'datetime-local', {required: true, max: localDateInput(new Date())})}
                </div>
                <p>{t.gettext('Dates use your local time and are sent in UTC. Maximum period: 31 days.')}</p>
                <button type='submit' disabled={pending || needsUserSelection}>{t.gettext(pending ? 'Generating report...' : 'Generate report')}</button>
            </fieldset>
        </form>
        {error && <p className='validationError' role='alert'>{t.gettext(error)}</p>}
        {pending && <p role='status'>{t.gettext('Generating report...')}</p>}
        {report && <>
            <h2>{t.gettext('Report results')}</h2>
            <p>{t.gettext('Period')}: {date(report.periodFrom)} – {date(report.periodTo)}</p>
            <p>{t.gettext('Generated at')}: {date(report.generatedAt)}</p>
            <p>{t.gettext('Sensors')}: {(report.sensors || []).length}</p>
            {(report.sensors || []).length === 0 && <p>{t.gettext('No sensors match the selected parameters.')}</p>}
            <div className='validationTable'><table><thead><tr>
                {['Sensor', 'Overall status', 'Measurement stability', 'Stability score', 'Delivery ratio (%)', 'Temperature validation', 'Temperature relevance score'].map(title => <th key={title}>{t.gettext(title)}</th>)}
            </tr></thead><tbody>{(report.sensors || []).map(sensor => <tr key={sensor.sensorId}>
                <td><a href={`#validation-sensor-${sensor.sensorId}`}>{sensor.name || sensor.sensorId}</a></td>
                <td>{label(sensor.overallStatus)}</td><td>{label(sensor.stability && sensor.stability.status)}</td>
                <td>{sensor.overallStatus === 'NOT_APPLICABLE' ? '—' : value('score', sensor.stability && sensor.stability.score)}</td>
                <td>{sensor.overallStatus === 'NOT_APPLICABLE' ? '—' : value('deliveryRatio', sensor.stability && sensor.stability.deliveryRatio)}</td>
                <td>{label(sensor.temperature ? sensor.temperature.status : 'NOT_APPLICABLE')}</td><td>{value('relevanceScore', sensor.temperature && sensor.temperature.relevanceScore)}</td>
            </tr>)}</tbody></table></div>
            {(report.sensors || []).map(sensor => <article key={sensor.sensorId} id={`validation-sensor-${sensor.sensorId}`}>
                <h3>{sensor.name || sensor.sensorId} · {label(sensor.overallStatus)}</h3>
                {metrics(sensor, {sensorId: 'Sensor ID', loraIdentifier: 'LoRa identifier', nodeType: 'Node type', fieldId: 'Field ID', lat: 'Latitude', lon: 'Longitude', distanceKm: 'Distance (km)', groupId: 'Comparison group'})}
                <p>{t.gettext('Location source')}: {sensor.locationSource ? t.gettext(sensor.locationSource === 'SENSOR' ? 'Sensor location' : sensor.locationSource === 'FIELD' ? 'Field location' : 'No data') : '—'}</p>
                {sensor.temperatureNotApplicableReason && <p>{t.gettext('Temperature not applicable reason')}: {label(sensor.temperatureNotApplicableReason)}</p>}
                <ul>{[...new Set([...(sensor.messages || []), ...((sensor.temperature && sensor.temperature.flags) || [])])].map(message => <li key={message}>{label(message)}</li>)}</ul>
                {sensor.overallStatus !== 'NOT_APPLICABLE' && <>
                    <h4>{t.gettext('Measurement stability')}: {label(sensor.stability && sensor.stability.status)}</h4>
                    {sensor.stability && metrics(sensor.stability, stabilityMetrics)}
                    {sensor.stability && <IntervalHistogram rows={sensor.stability.intervalHistogram} t={t}/>}
                    {sensor.stability && <><h4>{t.gettext('Cadence changes')}</h4>
                        {(sensor.stability.cadenceChanges || []).length === 0 ? <p>{t.gettext('No cadence changes')}</p> : <ul>
                            {sensor.stability.cadenceChanges.map((change, index) => <li key={index}>{date(change.at)} · {value('fromMin', change.fromMin)} → {value('toMin', change.toMin)} {t.gettext('minutes')}</li>)}
                        </ul>}</>}
                    <h4>{t.gettext('Temperature validation')}: {label(sensor.temperature ? sensor.temperature.status : 'NOT_APPLICABLE')}</h4>
                    {sensor.temperature && <>{metrics(sensor.temperature, temperatureMetrics)}
                        <h4>{t.gettext('Temperature source counts')}</h4>
                        <dl className='validationMetrics'>{Object.entries(sensor.temperature.temperatureSourceCounts || {}).map(([source, count]) => <div key={source}><dt>{source}</dt><dd>{value('count', count)}</dd></div>)}</dl>
                        <p>{t.gettext('Humidity applicable')}: {t.gettext(sensor.temperature.humidityApplicable ? 'Yes' : 'No')}</p>
                        {sensor.temperature.profile && <><h4>{t.gettext('Temperature profile')}: {label(sensor.temperature.profile.label)}</h4>
                            {metrics(sensor.temperature.profile, {nightColdestShare: 'Coldest night hours (%)', nightWarmestShare: 'Warmest night hours (%)', avgNightRankPercentile: 'Average night rank percentile (%)'})}</>}
                    </>}
                </>}
            </article>)}
            <h2>{t.gettext('Comparison groups')}</h2>
            {(report.groups || []).map(group => <article key={group.groupId}>
                <h3>{t.gettext('Comparison group')} #{group.groupId}</h3>
                {metrics(group, {centroidLat: 'Centroid latitude', centroidLon: 'Centroid longitude', referenceRadiusKm: 'Reference radius (km)'})}
                <p>{t.gettext('Member sensor IDs')}: {(group.memberSensorIds || []).join(', ')}</p>
                <p>{t.gettext('Extended reference pool')}: {t.gettext(group.referenceExtended ? 'Yes' : 'No')}</p>
                <h4>{t.gettext('External references')}</h4>
                {(group.externalReferences || []).length === 0 ? <p>{t.gettext('No external references')}</p> : <ul>
                    {group.externalReferences.map(ref => <li key={ref.sensorId}>{ref.name} (#{ref.sensorId}) · {value('distanceKm', ref.distanceKm)} km</li>)}
                </ul>}
            </article>)}
        </>}
    </section>;
};
export default SensorValidation;
